import { Errors } from '@app/types/global';
import { DEFAULT_LANGUAGE } from '@app/utils/languages';
import dayjs, { Dayjs } from 'dayjs';

import { GenericObject, MultiLingualObj } from '@app/components/common/Table/DefaultTable/types';

import { getTomorrowsDate } from './dateUtils';

export enum FIELD_TYPES {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  MULTILINGUAL = 'MULTILINGUAL',
  MULTILINGUAL_ARRAY = 'MULTILINGUAL_ARRAY',
  SELECT = 'SELECT',
  EMAIL = 'EMAIL',
  PASSWORD = 'PASSWORD',
  CONFIRM_PASSWORD = 'CONFIRM_PASSWORD',
  FUTURE_DATE = 'FUTURE_DATE',
  CUSTOM_VALIDATION = 'CUSTOM_VALIDATION',
}

const isEmptyString = (str: string | null | undefined) => {
  return !str || str.trim() === '';
};

const isEmailValid = (str: string) => {
  return str.includes('@');
};

const isEmptyMultiLingualString = (value: MultiLingualObj) => {
  return isEmptyString(value ? value[DEFAULT_LANGUAGE] : null);
};

const isEmptyMultiLingualArray = (value: any) => {
  if (!value || value.length === 0) {
    return true;
  }
  const arr = value[DEFAULT_LANGUAGE];
  // Final case is for when a user has added selections, then removed them
  return !arr || arr.length === 0 || (arr.length === 1 && arr[0] === '');
};

interface Field {
  key: string;
  type: FIELD_TYPES;
  message: string;
  nonRequired?: boolean;
  image?: File;
  shouldValidate?(values: GenericObject): boolean;
  customValidation?: Function;
}

interface RequiredFieldsInterface {
  fields: Field[];
  values: GenericObject;
  image?: File | undefined;
}

interface RequiredFieldInterface {
  errors: Errors;
  field: Field;
  values: GenericObject;
  image?: File | undefined;
}

export interface NonRequiredField {
  key: string;
  isMultilingual?: boolean;
  emptyValue: unknown;
}

export const validateFormField = ({ field, values, image, errors }: RequiredFieldInterface) => {
  if (FIELD_TYPES.CUSTOM_VALIDATION === field.type) {
    if (field.customValidation) {
      const result = field.customValidation(values[field.key]);
      if (result.validationResult === false) {
        errors[field.key] = result.validationMessage;
      }
      return;
    }
  }
  if (FIELD_TYPES.MULTILINGUAL === field.type) {
    if (isEmptyMultiLingualString(values[field.key] as MultiLingualObj)) {
      errors[field.key] = field.message;
    }
  } else if (FIELD_TYPES.MULTILINGUAL_ARRAY === field.type) {
    if (isEmptyMultiLingualArray(values[field.key] as MultiLingualObj)) {
      errors[`${field.key}.${DEFAULT_LANGUAGE}`] = field.message;
    }
  } else if (FIELD_TYPES.IMAGE === field.type) {
    if (!image) {
      errors[field.key] = field.message;
    }
  } else if (FIELD_TYPES.SELECT === field.type) {
    if (!values[field.key]) {
      errors[field.key] = field.message;
    }
  } else if (FIELD_TYPES.EMAIL === field.type && field.nonRequired) {
    if (values[field.key] === null) {
      values[field.key] = '';
    }
    if (!isEmptyString(values[field.key] as string) && !isEmailValid(values[field.key] as string)) {
      errors[field.key] = field.message;
    }
  } else if (FIELD_TYPES.EMAIL === field.type && !field.nonRequired) {
    if (isEmptyString(values[field.key] as string) || !isEmailValid(values[field.key] as string)) {
      errors[field.key] = field.message;
    }
  } else if (FIELD_TYPES.PASSWORD === field.type) {
    if (isEmptyString(values[field.key] as string)) {
      errors[field.key] = 'Please enter a password';
    } else if ((values[field.key] as string).length < 8) {
      errors[field.key] = 'Please use a password that is at least eight characters long';
    }
  } else if (FIELD_TYPES.CONFIRM_PASSWORD === field.type) {
    if (isEmptyString(values[field.key] as string)) {
      errors[field.key] = 'Please confirm your password';
      // Compare password and "confirm password" fields
    } else if (values[field.key] !== values['password']) {
      errors[field.key] = 'Please make sure the password and confirmed password match';
    }
  } else if (FIELD_TYPES.FUTURE_DATE === field.type) {
    // When you open a fresh page, for some reason this field
    // is a Date object. When you use the Custom Date Picker it becomes
    let date: Date = values[field.key] as Date;
    if (date && dayjs.isDayjs(date)) {
      date = (values[field.key] as Dayjs).toDate();
    }
    if (date && date < getTomorrowsDate()) {
      errors[field.key] = field.message;
    }
  } else {
    if (isEmptyString(values[field.key] as string)) {
      errors[field.key] = field.message;
    }
  }
};

export const formatNonRequiredMultiLingualFields = (
  values: GenericObject,
  nonRequiredMultilingualFields?: string[] | null
) => {
  nonRequiredMultilingualFields &&
    nonRequiredMultilingualFields.forEach((field: string) => {
      if (!values[field] || values[field] === '') {
        values[field] = { [DEFAULT_LANGUAGE]: '' };
      }
    });
};

const getFieldEmptyValue = (field: NonRequiredField) => {
  if (field.isMultilingual) {
    return { [DEFAULT_LANGUAGE]: field.emptyValue };
  } else {
    return field.emptyValue;
  }
};

export const formatNonRequiredFields = (values: GenericObject, nonRequired?: NonRequiredField[] | null) => {
  nonRequired &&
    nonRequired.forEach((field: NonRequiredField) => {
      if (!values[field.key] || values[field.key] === '') {
        values[field.key] = getFieldEmptyValue(field);
      }
    });
};

export const validateFormFields = ({ fields, values, image }: RequiredFieldsInterface) => {
  const errors: Errors = {};

  fields.forEach((field) => {
    validateFormField({ field, errors, values, image });
  });

  return errors;
};

export const validateConditionalFields = ({ fields, values }: RequiredFieldsInterface) => {
  const errors: Errors = {};

  fields.forEach((field) => {
    if (field.shouldValidate && !field.shouldValidate(values)) {
      return;
    }
    validateFormField({ field, errors, values });
  });

  return errors;
};
